import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ValueOf } from 'fym-common/src/types/utilityTypes';
import { RawWidgetData } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { useUpdateWidgetMutation } from '../../../../redux/api/fymAPI';
import { useEditorSaves } from '../saves/useEditorSaves';

/** A field change saves after this much time with no new change (design d0). */
export const AUTOSAVE_DELAY_MS = 600;

/**
 * The save state of an open block (design d0, 8): `saving` from the first change until the answer,
 * `saved`, `invalid` (a field is wrong, nothing is sent) or `failed` (the call failed).
 */
export type BlockSaveStatus = 'idle' | 'saving' | 'saved' | 'invalid' | 'failed';

interface UseBlockAutosaveProps {
  accountId: number;
  smartLinkId: number;
  widget: SerializedWidget;
}

/**
 * Autosave of a block form (owner decision 5): every change PATCHes the draft after 600 ms with no
 * new change. Done and the end of the form send a change that waits at once.
 */
export function useBlockAutosave({ accountId, smartLinkId, widget }: UseBlockAutosaveProps) {
  const [updateWidget] = useUpdateWidgetMutation();
  const saves = useEditorSaves();
  const saverKey = `block-autosave-${useId()}`;
  const [status, setStatus] = useState<BlockSaveStatus>('idle');
  // True when the last save reached the live page by itself (Catalog links: the campaign).
  const [isLive, setIsLive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitingRef = useRef<ValueOf<RawWidgetData> | null>(null);
  const widgetRef = useRef(widget);
  widgetRef.current = widget;
  const flushRef = useRef<() => void>(() => undefined);

  const send = useCallback(() => {
    timerRef.current = null;
    saves?.unregister(saverKey);
    const data = waitingRef.current;
    waitingRef.current = null;
    if (data === null) {
      return;
    }
    const { current } = widgetRef;
    const call = updateWidget({
      accountId,
      smartLinkId,
      widgetUUID: current.uuid,
      position: current.position,
      type: current.type,
      data,
    }).unwrap();
    (saves?.track(call) ?? call)
      .then(() => {
        // A newer change waits: its own answer sets the state.
        if (waitingRef.current === null) {
          setIsLive(false);
          setStatus('saved');
        }
      })
      .catch(() => setStatus('failed'));
  }, [accountId, saverKey, saves, smartLinkId, updateWidget]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    saves?.unregister(saverKey);
  }, [saverKey, saves]);

  /** A valid change: it saves after the delay. */
  const save = useCallback(
    (data: ValueOf<RawWidgetData>) => {
      clearTimer();
      saves?.setInvalid(saverKey, false);
      waitingRef.current = data;
      setStatus('saving');
      timerRef.current = setTimeout(send, AUTOSAVE_DELAY_MS);
      // Publish sends the change at once instead of waiting for the delay.
      saves?.register(saverKey, () => flushRef.current());
    },
    [clearTimer, saverKey, saves, send]
  );

  /**
   * A wrong field: nothing is sent, and the foot says "Not saved". The editor holds the publish until
   * the field is right again or the form closes (QA finding brk-16).
   */
  const markInvalid = useCallback(() => {
    clearTimer();
    waitingRef.current = null;
    saves?.setInvalid(saverKey, true);
    setStatus('invalid');
  }, [clearTimer, saverKey, saves]);

  /** Another call saved the change (a pre-save campaign). `live`: fans see it with no publish. */
  const markSaved = useCallback(
    (live: boolean) => {
      saves?.setInvalid(saverKey, false);
      setIsLive(live);
      setStatus('saved');
    },
    [saverKey, saves]
  );

  const markSaving = useCallback(() => setStatus('saving'), []);
  const markFailed = useCallback(() => setStatus('failed'), []);

  /** Sends a change that waits now (Done, the row closes). */
  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimer();
      send();
    }
  }, [clearTimer, send]);

  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);
  // A closed form keeps no wrong field: the block keeps its saved value.
  useEffect(() => () => saves?.setInvalid(saverKey, false), [saverKey, saves]);

  return { status, isLive, save, markInvalid, markSaved, markSaving, markFailed, flush };
}

export type BlockAutosave = ReturnType<typeof useBlockAutosave>;
